import { Classes, Colors, Dialog, Intent } from "@blueprintjs/core";
import {
  FGCustomInput,
  FGEmpty,
  FGListen,
  FGNumberInput,
  FGSelectInput,
  FGTextAreaInput,
  FGTextInput,
  FieldGroup,
} from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import styled from "styled-components";
import * as Yup from "yup";

import { DogApi, DogDto, DogPedigreeApi, EDogSex, EDogType, EKennelType, KennelApi } from "../../../../../api";
import assets from "../../../../../assets";
import {
  BoneIcon,
  FGCountrySelect,
  FGDogSearchInput,
  FGSRSHCheckboxInput,
  FGSRSHDateMaskInput,
  SharepointButton,
  SmallFormGenerator,
  UploadSharepointButton,
} from "../../../../../components";
import { useAuth, useDialog, useGlobalData } from "../../../../../contexts";
import { ERoutes } from "../../../../../ERoutes";
import { useApiService, useCrudApi, useTl, useWindow } from "../../../../../hooks";
import { useReferential } from "../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../locales";
import { DogChipNumersList } from "./chipNumber/DogChipNumersList";
import { DogPedigreesList } from "./Pedigree/DogPedigreesList";
import { DogBreederList } from "./People/DogBreederList";

import { DogKennelOwnersList } from "./People/DogKennelOwnersList";
import { DogOwnerListPage } from "./People/DogOwnerListPage";
import { toUpperCamelCase } from "../../../../../utils";

export interface IDogDetailPageProps {
  dogId: number;
  editMode: boolean;
}

const StyledContainer = styled.div`
  & > * {
    margin-top: 2rem;
  }
`;

const StyledUppperCamelCaseContainer = styled.div`
  * > * {
    text-transform: capitalize;
  }
`;

const ImgContainer = styled.div`
  position: absolute;
  top: -46px;
  right: 1%;
`;

export const DogDetailPage: React.FunctionComponent<IDogDetailPageProps> = ({ dogId, editMode }) => {
  const [kennelType, setkennelType] = React.useState<EKennelType>();
  const [kennelName, setkennelName] = React.useState<string>();
  const [dogTypeCode, setDogTypeCode] = React.useState<any>();
  const [breedRequest, setBreedRequest] = React.useState(0);
  const [prefixCountryIdRequest, setPrefixCountryIdRequest] = React.useState(0);
  const [countryRequired, setCountryRequired] = React.useState<boolean>(false);
  


  const { showDialogPromise } = useDialog();
  const { t } = useTl();
  const history = useHistory();
  const { currentLanguage } = useGlobalData();
  const { userPermissions } = useAuth();
  const api = useApiService(DogApi);
  const pedigreeApi = useApiService(DogPedigreeApi);
  const kennelApi = useApiService(KennelApi);

  const saveCallback = React.useCallback(
    async (d: any) => {
      console.log("birthDate ?", d.birthDate);
      if (dogId <= 0) {
        // TODO-CRR : dialogresult ignored? if we push yes or no, the result will be the
        await showDialogPromise({
          message: t(ETLCodes.PleaseDontForgetToAddChipNumber),
          title: t(ETLCodes.Warning),
          buttons: [
            {
              resultType: "ok",
              text: t(ETLCodes.General_Ok),
              intent: Intent.PRIMARY,
            },
          ],
        });
      }
      d.firstName = toUpperCamelCase(d.firstName);
      return await api.save(d);
    },
    [api, dogId, showDialogPromise, t]
  );

  const { data, loading, saving, saveItem, deleting, deleteItem, refresh } = useCrudApi<DogDto>(
    React.useMemo(
      () => ({
        getApiFn: (id = dogId) => (id > 0 ? api.getDog(id) : new DogDto({ remark: "", exclusionReason: "" }).toJSON()),
        saveApiFn: (d: DogDto) => saveCallback(d),
        onSavedRoute: (d) => `${ERoutes.dog}/${d.dogId}/detail/edit`,
        deleteApiFn: (d) => api.delete(d.dogId),
        onDeletedRoute: () => ERoutes.dog,
      }),
      [api, dogId, saveCallback]
    ),
    [dogId]
  );

 

  const [types, tloading] = useReferential(
    (a) =>
      userPermissions.some(
        (p) =>
          p.module.toUpperCase() === "DOG" &&
          p.permission.toUpperCase() === "E" &&
          p.tab.toUpperCase() === "ADDBELGIANWITHPEDIGREE"
      )
        ? a.getAllDogTypes(currentLanguage.languageId)
        : a.getDogTypes(currentLanguage.languageId),
    true,
    []
  );
  const [colors, coloading] = useReferential((a) => a.getColorsByBreed(breedRequest), true, [breedRequest]);
  const [breeds, bloading] = useReferential((a) => a.getBreeds(null), true, []);
  const [kennels, kloading] = useReferential((a) => a.getKennels(), true, []);
  const [sex, sloading] = useReferential((a) => a.getSex(currentLanguage.languageId), false, []);

  const [prefixesByCountry] = useReferential((a) => a.getPedigreePrefixesByCountry(prefixCountryIdRequest, 0), true, [
    prefixCountryIdRequest,
  ]);
  const [prefixes, ploading] = useReferential((a) => a.getAllForeignPedigreePrefixes(), true, [prefixCountryIdRequest]);

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      dogTypeId: Yup.number()
        .nullable()
        .required(t(ETLCodes.Required))
        .test("allowed-change", t(ETLCodes.ChangeNotAllowed), async function (value) {
          if (dogId <= 0) return true;
          else {
            const result = await api.checkDogTypeChange(value, data?.dogTypeId);
            return result.value;
          }
        }),
      firstName: Yup.string().required(t(ETLCodes.Required)),
      birthDate: Yup.date()
        .nullable()
        .max(new Date(), t(ETLCodes.MustBeValidDateUntilToday))
        .when("dogTypeId", (value, schema) =>
          value && dogTypeCode && dogTypeCode === EDogType.LineageDog
            ? schema.nullable()
            : schema.required(t(ETLCodes.RequiredDate))
        ),
      breedId: Yup.number().nullable().required(t(ETLCodes.Required)),
      kennelId: Yup.number().nullable(),
      sex: Yup.string().nullable().required(t(ETLCodes.Required)),
      colorId: Yup.number()
        .nullable()
        .when("dogTypeId", (value, schema) =>
          value &&
          dogTypeCode &&
          [EDogType.ForeignDog, EDogType.DogWithoutPedigree, EDogType.ExpoDog, EDogType.LineageDog].includes(
            dogTypeCode
          )
            ? schema.nullable()
            : schema.required(t(ETLCodes.Required))
        ),
      countryId: Yup.number()
        .nullable()
        .when("dogTypeId", (value, schema) =>
          value && dogTypeCode && [EDogType.ExpoDog, EDogType.DogWithoutPedigree].includes(dogTypeCode)
            ? schema.nullable()
            : schema.required(t(ETLCodes.Required))
        ),
      countryPedigreePrefixId: Yup.number()
        .nullable()
        .when("dogTypeId", (value, schema) =>
          dogId > 0
            ? schema
            : value && dogTypeCode && [EDogType.ExpoDog, EDogType.DogWithoutPedigree].includes(dogTypeCode)
            ? schema.nullable()
            : schema.required(t(ETLCodes.Required))
        ),
      pedigreeNumber: Yup.string()
        .nullable()
        .when("dogTypeId", (value, schema) =>
          dogId > 0
            ? schema
            : value && dogTypeCode && [EDogType.ExpoDog, EDogType.DogWithoutPedigree].includes(dogTypeCode)
            ? schema
            : schema.required(t(ETLCodes.Required))
        )
        .test("pedigree-exists", t(ETLCodes.PedigreeExistsError), async function (value) {
          if (dogId < 1 && value) {
            const error = await pedigreeApi.pedigreeUnique(value);
            return error.value;
          }
          return true;
        }),
    });
  }, [api, data, dogId, dogTypeCode, pedigreeApi, t]);

  const icon = React.useMemo(() => {
    if (data?.deathDate) {
      return <i className="fas fa-cross" style={{ marginLeft: "1rem" }} />;
    }
    if (data?.exclusionEndDate > new Date()) {
      return <i className="fas fa-ban" style={{ marginLeft: "1rem" }} />;
    }
    return <></>;
  }, [data]);

  const eliteIcon = React.useMemo(() => {
    if (data?.isFromEliteBreeder) {
      return <BoneIcon style={{ marginLeft: "1rem" }} />;
    }
    return <></>;
  }, [data]);
  const { openNewTab } = useWindow();

  React.useEffect(() => {
    if (!kloading && !loading) {
      const name = kennels?.find((v) => v.value === data?.kennelId)?.label;
      setkennelName(name);
    }
  }, [data, kennelName, kennels, kloading, loading]);

  const [shpLoading, setShpLoading] = React.useState(false);
  const existsInShp = React.useMemo(() => !!data?.sharepointUri, [data?.sharepointUri]);
  const openOrCreateInShp = React.useCallback(async () => {
    setShpLoading(true);
    if (!!data?.sharepointUri) {
      window.open(data?.sharepointUri, "_blank");
      setShpLoading(false);
    } else {
      try {
        await api.createInSharepoint(dogId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setShpLoading(false);
      }
    }
  }, [api, data?.sharepointUri, dogId, refresh]);

  return (
    <>
      <div style={{ position: "relative" }}>
        <ImgContainer>
          {data?.isFci ? (
            <img src={assets.images.fci} alt="FCI logo" width={"75px"} height={"75px"} />
          ) : (
            <img src={assets.images.not_fci} alt={"Barred FCI Logo"} width={"75px"} height={"75px"} />
          )}
        </ImgContainer>
      </div>

      <SmallFormGenerator
        initialValues={data}
        onSubmit={saveItem}
        editMode={editMode}
        validationSchema={FormSchema}
        loading={loading}
        onCancel={() => history.push(ERoutes.dog)}
        onDelete={deleteItem}
        showDeleteButton={dogId > 0}
        saving={saving}
        deleting={deleting}
      >
        <FGListen
          field="dogTypeId"
          onChanged={async (value) => {
            const result = await api.getDogTypeCode(value);
            setCountryRequired(![EDogType.ExpoDog, EDogType.DogWithoutPedigree].includes(result));
            setDogTypeCode(result);
          }}
        />
        <FGListen
          field="countryId"
          onChanged={async (value, formik) => {
            if (!value) return;
            setPrefixCountryIdRequest(value);
            // if (value && value > 0) {
            //   const isBelgian = await countryApi.isBelgian(value);
            //   setNationality(isBelgian.value);
            // }
          }}
        />
        <FGListen
          field="breedId"
          onChanged={(value) => {
            if (value === null) {
              value = 0;
            }
            setBreedRequest(value);
          }}
        />

        <FGListen
          field="kennelId"
          onChanged={async (value) => {
            if (value) {
              const type = await kennelApi?.getKennelType(value);
              setkennelType(type);
              setkennelName(kennels?.find((v) => v.value === value)?.label);
            }
          }}
        />
        <FieldGroup columns={2}>
          <FieldGroup
            columns={1}
            fieldsetProps={{
              title: t(ETLCodes.DogInformations),
              rightElement: dogId > 0 && (
                <>
                  <SharepointButton existsInShp={existsInShp} loading={shpLoading} onClick={openOrCreateInShp} />
                </>
              ),
            }}
          >
            {dogId <= 0 || data?.dogTypeAuthorized ? (
              <FGSelectInput name="dogTypeId" label={t(ETLCodes.Type)} items={types} loading={tloading} />
            ) : (
              <FGCustomInput name="dogType" label={t(ETLCodes.Type)} helperText="">
                {(ctx) => <div>{data?.dogType}</div>}
              </FGCustomInput>
            )}
            <StyledUppperCamelCaseContainer>
              {" "}
              <FGTextInput name="firstName" label={t(ETLCodes.Name)} />
            </StyledUppperCamelCaseContainer>
            <FGSelectInput
              name="kennelId"
              label={t(ETLCodes.Kennel)}
              items={kennels}
              loading={kloading}
              onItemViewClick={(item) => openNewTab(`${ERoutes.kennel}/${item}/detail/view`)}
              readonly={
                dogTypeCode &&
                [EDogType.ImportDog, EDogType.DogWithoutPedigree, EDogType.ForeignDog].includes(dogTypeCode)
              }
            />
            <FGCustomInput name="deathDate" label={t(ETLCodes.CompleteName)} helperText="">
              {(ctx) => {
                if (dogId) {
                  return (
                    <div
                      style={{
                        paddingLeft: "10px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {kennelType === EKennelType.Prefix
                        ? `${kennelName ?? ""} ${toUpperCamelCase(ctx.formik.values?.firstName)}`
                        : `${toUpperCamelCase(ctx.formik.values?.firstName)} ${kennelName ?? ""}`}{" "}
                      {icon}
                      {eliteIcon}
                    </div>
                  );
                }
              }}
            </FGCustomInput>
            <FGSRSHDateMaskInput
              name="birthDate"
              label={t(ETLCodes.BirthDate)}
              requiredMark={dogTypeCode && dogTypeCode !== EDogType.LineageDog}
            />
            <FGSelectInput
              name="breedId"
              label={t(ETLCodes.Breed)}
              items={breeds}
              loading={bloading}
              onItemViewClick={(item) => openNewTab(`${ERoutes.breeds}/${item}/detail/view`)}
            />
            <FGSelectInput
              name="colorId"
              label={t(ETLCodes.Color)}
              items={colors}
              loading={coloading}
              onItemViewClick={(item) => openNewTab(`${ERoutes.color}/${item}/detail/view`)}
            />
            <FGSRSHDateMaskInput name="deathDate" label={t(ETLCodes.DeathDate)} />
            <FGSelectInput name="sex" label={t(ETLCodes.Sex)} items={sex} loading={sloading} requiredMark />
            <FGCountrySelect
              name="countryId"
              label={t(ETLCodes.Country)}
              onItemViewClick={(item) => openNewTab(`${ERoutes.country}/${item}/detail/view`)}
              requiredMark={dogTypeCode && ![EDogType.ExpoDog, EDogType.DogWithoutPedigree].includes(dogTypeCode)}
              includeBelgium={
                dogTypeCode !== EDogType.ForeignDog &&
                ((dogId > 0 && data?.countryId !== null) || (dogTypeCode && dogTypeCode === EDogType.ForeignDog))
                  ? true
                  : false
              }
              readonly={
                dogTypeCode !== EDogType.ForeignDog &&
                ((dogId > 0 && data?.countryId !== null) || (dogTypeCode && dogTypeCode === EDogType.ForeignDog))
              }
            />
            <FGSelectInput
              name="countryPedigreePrefixId"
              label={t(ETLCodes.Prefix)}
              items={countryRequired ? prefixesByCountry : prefixes}
              loading={ploading}
              requiredMark={dogTypeCode && ![EDogType.ExpoDog, EDogType.DogWithoutPedigree].includes(dogTypeCode)}
              readonly={dogId > 0}
            />
            <FGTextInput
              name="pedigreeNumber"
              label={t(ETLCodes.Pedigree)}
              requiredMark={dogTypeCode && ![EDogType.ExpoDog, EDogType.DogWithoutPedigree].includes(dogTypeCode)}
              readonly={dogId > 0}
            />
            <FGTextInput name="as400Pedigree" label={t(ETLCodes.As400Pedigree)} />
            <FieldGroup columns={2}>
              <FGSRSHCheckboxInput name="awaitingValidation" label={t(ETLCodes.AwaitingValidation)} />
            </FieldGroup>
            <FGTextInput name="tatoo" label={t(ETLCodes.Tatoo)} maxLength={80} />
            <FGTextAreaInput name="remark" label={t(ETLCodes.Remark)} />
          </FieldGroup>
          <FieldGroup columns={1} fieldsetProps={{ title: t(ETLCodes.OtherInformations) }}>
            <FGSRSHCheckboxInput name="dockedTail" label={t(ETLCodes.DockedTail)} />
            <FGEmpty />
            <FGTextInput name="internalNumber" label={t(ETLCodes.InternalNumber)} />
            <FGEmpty />
            <FGSRSHCheckboxInput name="socialBehaviour" label={t(ETLCodes.SocialBehaviour)} />
            <FGSelectInput
              name="socialCompetitionNumber"
              label={t(ETLCodes.Competition)}
              items={[]}
              loading={false}
              visible={(ctx) => ctx.formik.values?.socialBehaviour}
            />
            <FGSRSHCheckboxInput name="breedingAuthorized" label={t(ETLCodes.BreedingAuthorized)} />
            <FGSRSHCheckboxInput name="workCertificate" label={t(ETLCodes.WorkCertificate)} />
            <FGSRSHDateMaskInput name="exclusionStartDate" label={t(ETLCodes.ExclusionStartDate)} />
            <FGSRSHDateMaskInput name="exclusionEndDate" label={t(ETLCodes.ExclusionEndDate)} />
            <FGTextAreaInput name="exclusionReason" label={t(ETLCodes.ExclusionReason)} />
            <FGNumberInput label={t(ETLCodes.AmountTries)} name="numberOfImportTries" />
          </FieldGroup>
          <FieldGroup columns={1} fieldsetProps={{ title: t(ETLCodes.ParentInformations) }}>
            <FGSRSHCheckboxInput name="unknownFather" label={t(ETLCodes.UnknownFather)} />
            <FGDogSearchInput
              defaultSearch={{ sex: EDogSex.Male }}
              label={t(ETLCodes.Father)}
              name="fatherDogId"
              visible={(ctx) => !ctx.formik.values?.unknownFather}
              onItemViewClick={(item: number) => openNewTab(`${ERoutes.dog}/${item}/detail/view`)}
            />
            <FGSRSHCheckboxInput name="unknownMother" label={t(ETLCodes.UnknownMother)} />
            <FGDogSearchInput
              defaultSearch={{ sex: EDogSex.Female }}
              label={t(ETLCodes.Mother)}
              name="motherDogId"
              visible={(ctx) => !ctx.formik.values?.unknownMother}
              onItemViewClick={(item: number) => openNewTab(`${ERoutes.dog}/${item}/detail/view`)}
            />
          </FieldGroup>
        </FieldGroup>
      </SmallFormGenerator>
      {dogId > 0 && (
        <StyledContainer>
          <DogChipNumersList dogId={dogId} editMode={editMode} />
          <DogKennelOwnersList dogId={dogId} editMode={editMode} />
          <DogOwnerListPage dogId={dogId} editMode={editMode} dogBirthDate={data?.birthDate} />
          <DogBreederList dogId={dogId} editMode={editMode} />
          <DogPedigreesList dogId={dogId} editMode={editMode} refresh={() => refresh()} />
        </StyledContainer>
      )}
    </>
  );
};
